import { ButtonBase, makeStyles } from '@material-ui/core';
import {
  filter, flatten, forEach, get, includes, isEmpty, isNull, map, omit, uniq, uniqBy,
} from 'lodash';
import moment from 'moment';
import React, { useContext, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import config from '@/configs/config';
import Slider from '../components/Slider';
import menuContext from '../contexts/menu';
import titleImage from '../images/mobile.png';
import { FILTER } from '../constants';

const useTitleStyle = makeStyles((theme) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
  },
  iconContainer: {
    height: 41,
  },
  icon: {
    height: '100%',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    marginLeft: theme.spacing(1),
  },
  description: {
    fontSize: 12,
    fontWeight: 'normal',
  },
}));

function Title() {
  const classes = useTitleStyle();

  return (
    <div className={classes.root}>
      <div className={classes.iconContainer}>
        <img className={classes.icon} src={titleImage} alt="" />
      </div>
      <div className={classes.content}>
        <span>預訂商品｜排隊名店必敗 SELECT</span>
        <span className={classes.description}>線上訂購，指定時間至機台輸入取貨碼領取。</span>
      </div>
    </div>
  );
}

const useItemStyle = makeStyles((theme) => ({
  root: {
    alignItems: 'stretch',
    background: 'white',
    borderRadius: 8,
    boxShadow: 'rgba(157,157,157,.5) 1px 1px 5px 0px',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
    padding: theme.spacing(1),
    position: 'relative',
    textAlign: 'left',
    width: '100%',
  },
  coverImageContainer: {
    position: 'relative',
    paddingBottom: '50%',
  },
  coverImage: {
    height: '100%',
    position: 'absolute',
    top: 0,
    width: '100%',
  },
  name: {
    background: theme.palette.campaign.main,
    borderRadius: 8,
    color: 'white',
    fontSize: 14,
    fontWeight: 'bold',
    marginTop: theme.spacing(1),
    padding: theme.spacing(0, 1),
  },
  content: {
    alignItems: 'stretch',
    display: 'flex',
    flexDirection: 'column',
    fontSize: 16,
    fontWeight: 'bold',
    marginTop: theme.spacing(1),
  },
  providerName: {
  },
  productName: {
    flexShrink: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  sellPrice: {
    fontSize: 12,
  },
  price: {
    alignItems: 'flex-end',
    display: 'flex',
    fontSize: 14,
    flexShrink: 0,
    marginLeft: theme.spacing(1),
  },
  contentFirst: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
  },
  contentSecond: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  labelContainer: {
    flexShrink: 0,
  },
  coverLabel: {
    background: theme.palette.preOrder.main,
    borderRadius: 6,
    color: '#ffffff',
    fontSize: 12,
    fontWeight: 'bold',
    padding: theme.spacing(0.5, 1),
    whiteSpace: 'nowrap',
  },
  redeemText: {
    color: 'rgb(148,139,139)',
    fontSize: 13,
    marginLeft: theme.spacing(1),
    whiteSpace: 'nowrap',
  },
  onSale: {
    color: theme.palette.price.onSale,
    fontFamily: 'Roboto',
    marginLeft: theme.spacing(1),
  },
}));

function Item(data) {
  const {
    name,
    onClick,
    price,
    productNumber,
    provider,
    redeemStartTime,
    sellPrice,
  } = data;
  const classes = useItemStyle();

  return (
    <ButtonBase
      className={classes.root}
      onClick={() => {
        onClick(omit(data, 'onClick'));
      }}
    >
      <div className={classes.coverImageContainer}>
        <img
          alt=""
          className={classes.coverImage}
          src={`${config.RESOURCE_URI}ProductImages/${productNumber}_PO_cover.jpg`}
          onError={(e) => {
            e.currentTarget.src = `${config.RESOURCE_URI}provider_default.png`;
          }}
        />
      </div>
      <div className={classes.content}>
        <div className={classes.contentFirst}>
          <span className={classes.providerName}>{`【${provider}】`}</span>
          <div className={classes.labelContainer}>
            <span className={classes.coverLabel}>預訂</span>
            <span className={classes.redeemText}>{`${moment(redeemStartTime).utcOffset(8).format('M/D')}到`}</span>
          </div>
        </div>
        <div className={classes.contentSecond}>
          <span className={classes.productName}>{name}</span>
          <div className={classes.price}>
            {
            price > sellPrice
              ? (
                <>
                  <span style={{
                    fontSize: 12,
                    textDecoration: 'line-through',
                    fontFamily: 'Roboto',
                  }}
                  >
                    {`$${price}`}
                  </span>
                  <span className={classes.onSale}>{`$${sellPrice}`}</span>
                </>
              ) : (
                `$${price}`
              )
          }
          </div>
        </div>
      </div>
    </ButtonBase>
  );
}

export default function OnlineSelection({ data, onClick }) {
  const { date: dateString } = useParams();
  const { menuCache } = useContext(menuContext);
  const { fridgeId: selectedFridgeId } = useParams();
  const { currentMenuArrangementDaily, fridges } = menuCache || {};

  const preOrderSelectionIds = useMemo(() => uniq(flatten(map(
    filter(currentMenuArrangementDaily, ({ startTime, endTime }) => moment(dateString).isBetween(startTime, endTime, undefined, '[]')),
    'productIds'
  ))), [currentMenuArrangementDaily, dateString]);

  const products = useMemo(() => {
    const result = [];
    forEach(preOrderSelectionIds, (id) => {
      const product = data.find((item) => item.id === id);
      const { onlineShopId, redeemDateLatestRegisterTimeDict, filterDict } = product || {};

      if (
        isEmpty(onlineShopId)
        || isEmpty(redeemDateLatestRegisterTimeDict)
        || !get(filterDict, FILTER.BESTSELLER, false)
        || !includes(preOrderSelectionIds, id)
      ) return;

      let latestRegisterTime = null;
      let redeemStartTime = null;

      forEach(
        redeemDateLatestRegisterTimeDict,
        (registerTime, redeemDate) => {
          if (moment().isAfter(registerTime)) return;
          if (
            registerTime < latestRegisterTime
                    || isNull(latestRegisterTime)
          ) {
            latestRegisterTime = registerTime;
            redeemStartTime = +moment(redeemDate).utcOffset(8).startOf('day').set('hour', 12);
          }
        }
      );

      if (
        isNull(latestRegisterTime)
        || isNull(redeemStartTime)
      ) return;

      product.fridgeId = selectedFridgeId;
      product.fridgeName = get(fridges, [selectedFridgeId, 'name']);
      product.redeemStartTime = redeemStartTime;
      product.redeemEndTime = +moment(redeemStartTime)
        .utcOffset(8)
        .startOf('day')
        .add(1, 'day')
        .set('hour', 11);

      result.push(product);
    });
    return result;
  }, [data, selectedFridgeId, fridges, preOrderSelectionIds]);

  const uniqueProducts = useMemo(() => uniqBy(products, 'id'), [products]);

  return isEmpty(uniqueProducts) ? null : (
    <Slider
      data={uniqueProducts}
      renderItem={(itemProps) => <Item {...itemProps} onClick={onClick} />}
      title={<Title />}
    />
  );
}
